import React, { useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Card } from "primereact/card";
import { Message } from "primereact/message";

const AddCategoryForm = ({ onAddCategory, onCancel, onUploadImage }) => {
  const [category, setCategory] = useState({
    name: "",
    description: "",
    services: [],
  });
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [imageUploading, setImageUploading] = useState({}); // Track per-service image uploads

  const handleServiceChange = (index, field, value) => {
    const updated = [...category.services];
    updated[index][field] = value;
    setCategory({ ...category, services: updated });
  };

  const handleServiceImageUpload = async (event, serviceIndex) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Set uploading state for this service
    setImageUploading(prev => ({ ...prev, [serviceIndex]: true }));

    try {
      const updated = [...category.services];
      
      // Initialize arrays if they don't exist
      if (!updated[serviceIndex].imageIds) {
        updated[serviceIndex].imageIds = [];
      }
      if (!updated[serviceIndex].imagePreviews) {
        updated[serviceIndex].imagePreviews = [];
      }

      // Upload each file immediately and get the ID
      for (const file of files) {
        try {
          const uploadResult = await onUploadImage(file);
          
          if (uploadResult.success) {
            // Create preview
            const previewUrl = URL.createObjectURL(file);
            
            // Update the service with the new image ID and preview
            updated[serviceIndex].imageIds.push(uploadResult.image.id);
            updated[serviceIndex].imagePreviews.push({
              id: uploadResult.image.id,
              preview: previewUrl,
              fileName: file.name
            });
            
            console.log(`✅ Image uploaded for service ${serviceIndex + 1}:`, uploadResult.image.id);
          } else {
            console.error(`❌ Failed to upload image for service ${serviceIndex + 1}:`, uploadResult.error);
          }
        } catch (error) {
          console.error(`❌ Error uploading image for service ${serviceIndex + 1}:`, error);
        }
      }

      setCategory({ ...category, services: updated });
      
    } catch (error) {
      console.error("Error in image upload process:", error);
    } finally {
      // Clear uploading state for this service
      setImageUploading(prev => ({ ...prev, [serviceIndex]: false }));
      // Clear the file input
      event.target.value = '';
    }
  };

  const removeServiceImage = (serviceIndex, imageIndex) => {
    setCategory(prev => {
      const updated = [...prev.services];
      
      // Clean up the preview URL
      if (updated[serviceIndex].imagePreviews?.[imageIndex]?.preview) {
        URL.revokeObjectURL(updated[serviceIndex].imagePreviews[imageIndex].preview);
      }
      
      // Remove from both arrays
      updated[serviceIndex].imageIds.splice(imageIndex, 1);
      updated[serviceIndex].imagePreviews.splice(imageIndex, 1);
      
      return { ...prev, services: updated };
    });
  };

  const addNewService = () =>
    setCategory({
      ...category,
      services: [
        ...category.services,
        { 
          service_name: "", 
          description: "", // CHANGED BACK: from "service_description" to "description"
          imageIds: [], // Store uploaded image IDs
          imagePreviews: [] // Store previews with IDs
        },
      ],
    });

  const removeService = (index) => {
    const updated = [...category.services];
    
    // Clean up all preview URLs for this service
    if (updated[index].imagePreviews) {
      updated[index].imagePreviews.forEach(preview => {
        if (preview.preview) {
          URL.revokeObjectURL(preview.preview);
        }
      });
    }
    
    updated.splice(index, 1);
    setCategory({ ...category, services: updated });
    
    // Clear errors for removed service
    const newErrors = { ...errors };
    Object.keys(newErrors).forEach(key => {
      if (key.includes(`_${index}`)) {
        delete newErrors[key];
      }
    });
    setErrors(newErrors);
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Category validation
    if (!category.name.trim()) newErrors.name = "Category name is required.";
    if (!category.description.trim())
      newErrors.description = "Category description is required.";

    // Services validation - images are optional
    category.services.forEach((srv, i) => {
      if (!srv.service_name.trim())
        newErrors[`service_name_${i}`] = `Service ${i + 1} name is required.`;
      if (!srv.description.trim()) // CHANGED: from service_description to description
        newErrors[`description_${i}`] = `Service ${i + 1} description is required.`; // CHANGED: from service_description to description
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setUploading(true);
    
    try {
      // Prepare payload with correct field names for API
      const payload = {
        name: category.name,
        description: category.description,
        services: category.services.map(service => ({
          service_name: service.service_name,
          description: service.description, // CHANGED: from service.service_description to service.description
          image_ids: service.imageIds || [], // Use the already uploaded image IDs
        })),
      };

      console.log("Submitting category with services:", payload);
      await onAddCategory(payload);

      // Reset form on success
      setCategory({ name: "", description: "", services: [] });
      setErrors({});
      
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setUploading(false);
    }
  };

  // Handle form reset when canceled
  const handleCancel = () => {
    // Clean up all preview URLs
    category.services.forEach(service => {
      if (service.imagePreviews) {
        service.imagePreviews.forEach(preview => {
          if (preview.preview) {
            URL.revokeObjectURL(preview.preview);
          }
        });
      }
    });
    
    setCategory({ name: "", description: "", services: [] });
    setErrors({});
    onCancel();
  };

  // VisuallyHiddenInput component
  const VisuallyHiddenInput = ({ ...props }) => (
    <input
      {...props}
      style={{
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: 1,
        overflow: 'hidden',
        position: 'absolute',
        bottom: 0,
        left: 0,
        whiteSpace: 'nowrap',
        width: 1,
      }}
    />
  );

  return (
    <Card title="Add Category / Services" className="p-4 mb-4">
      <div className="p-fluid p-formgrid p-grid">
        {/* Category Name */}
        <div className="p-field p-col-12 p-md-6">
          <label>Category Name *</label>
          <InputText
            value={category.name}
            onChange={(e) => setCategory({ ...category, name: e.target.value })}
            className={errors.name ? "p-invalid" : ""}
            placeholder="Enter category name"
            disabled={uploading}
          />
          {errors.name && <Message severity="error" text={errors.name} />}
        </div>

        {/* Category Description */}
        <div className="p-field p-col-12">
          <label>Category Description *</label>
          <InputTextarea
            rows={3}
            value={category.description}
            onChange={(e) =>
              setCategory({ ...category, description: e.target.value })
            }
            className={errors.description ? "p-invalid" : ""}
            placeholder="Enter category description"
            disabled={uploading}
          />
          {errors.description && (
            <Message severity="error" text={errors.description} />
          )}
        </div>

        {/* Services Section */}
        {category.services.map((srv, i) => (
          <Card key={i} title={`Service ${i + 1}`} className="mt-3 p-3" subTitle="All fields except images are required">
            <div className="p-field">
              <label>Service Name *</label>
              <InputText
                value={srv.service_name}
                onChange={(e) =>
                  handleServiceChange(i, "service_name", e.target.value)
                }
                className={errors[`service_name_${i}`] ? "p-invalid" : ""}
                placeholder="Enter service name"
                disabled={uploading}
              />
              {errors[`service_name_${i}`] && (
                <Message severity="error" text={errors[`service_name_${i}`]} />
              )}
            </div>

            <div className="p-field mt-2">
              <label>Service Description *</label>
              <InputTextarea
                rows={2}
                value={srv.description} // CHANGED: from service_description to description
                onChange={(e) =>
                  handleServiceChange(i, "description", e.target.value) // CHANGED: from service_description to description
                }
                className={
                  errors[`description_${i}`] ? "p-invalid" : "" // CHANGED: from service_description to description
                }
                placeholder="Enter service description"
                disabled={uploading}
              />
              {errors[`description_${i}`] && ( // CHANGED: from service_description to description
                <Message
                  severity="error"
                  text={errors[`description_${i}`]} // CHANGED: from service_description to description
                />
              )}
            </div>

            {/* Optional Multiple Image Upload */}
            <div className="p-field mt-3">
              <label>Service Images (Optional) - Multiple allowed</label>
              <div className="flex flex-column gap-2">
                {/* Custom Upload Interface */}
                <Button
                  label={
                    imageUploading[i] 
                      ? "Uploading..." 
                      : srv.imageIds?.length > 0 
                        ? "Add More Images" 
                        : "Choose Images"
                  }
                  icon="pi pi-cloud-upload"
                  className="p-button-outlined"
                  disabled={uploading || imageUploading[i]}
                  onClick={() => !(uploading || imageUploading[i]) && document.getElementById(`file-input-${i}`)?.click()}
                />
                <VisuallyHiddenInput 
                  id={`file-input-${i}`}
                  type="file" 
                  multiple 
                  accept="image/*"
                  onChange={(e) => handleServiceImageUpload(e, i)}
                  disabled={uploading || imageUploading[i]}
                />

                {/* Uploaded Images Preview */}
                {srv.imagePreviews && srv.imagePreviews.length > 0 && (
                  <div className="mb-2">
                    <div className="text-sm text-500 mb-1">
                      {srv.imagePreviews.length} image(s) uploaded
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {srv.imagePreviews.map((previewData, imgIndex) => (
                        <div key={previewData.id} className="relative">
                          <img 
                            src={previewData.preview} 
                            alt={`Preview ${imgIndex + 1}`}
                            className="border-round" 
                            style={{ 
                              width: '80px', 
                              height: '80px', 
                              objectFit: 'cover' 
                            }}
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1">
                            ID: {previewData.id.substring(0, 8)}...
                          </div>
                          <Button 
                            icon="pi pi-times" 
                            className="p-button-danger p-button-outlined p-button-sm absolute"
                            style={{ 
                              top: '-8px', 
                              right: '-8px',
                              width: '24px',
                              height: '24px'
                            }}
                            onClick={() => removeServiceImage(i, imgIndex)}
                            tooltip="Remove image"
                            disabled={uploading}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <small className="text-500">
                  Supported formats: JPG, PNG, JPEG, WEBP. Max size: 5MB per image. 
                  Images are uploaded immediately when selected.
                </small>
              </div>
            </div>

            {/* Remove Service Button */}
            <div className="flex justify-content-end mt-3">
              <Button
                label="Remove Service"
                icon="pi pi-trash"
                className="p-button-danger p-button-outlined p-button-sm"
                onClick={() => removeService(i)}
                disabled={uploading}
              />
            </div>
          </Card>
        ))}

        {/* Action Buttons */}
        <div className="p-col-12 mt-4 flex justify-content-between align-items-center">
          <div className="flex gap-2">
            <Button
              label="➕ Add Service"
              icon="pi pi-plus"
              className="p-button-info"
              onClick={addNewService}
              disabled={uploading}
            />
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={handleCancel}
              disabled={uploading}
            />
          </div>
          
          <Button
            label={uploading ? "Submitting..." : "Submit"}
            icon={uploading ? "pi pi-spin pi-spinner" : "pi pi-check"}
            className="p-button-success"
            onClick={handleSubmit}
            disabled={uploading}
          />
        </div>

        {/* Info Message */}
        <div className="p-col-12 mt-2">
          <Message 
            severity="info" 
            text="Note: Service images are optional and uploaded immediately. All other fields are required. Multiple images allowed per service." 
          />
        </div>
      </div>
    </Card>
  );
};

export default AddCategoryForm;